import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import socket from "services/SocketService";
import { notifySuccess } from "utils/toast";

// Keeps the employee's chat connection alive on EVERY page, not only the
// Dashboard. Leaving the Dashboard used to disconnect the socket, and the
// server marks a disconnected employee offline — so an "Online" employee
// browsing another page could never receive a queued chat.
//
// While not on the Dashboard it also handles incoming chat offers: when the
// employee is Online, it accepts (same as the Dashboard's auto-accept) and
// opens the Dashboard, which resumes the chat.

const getAdmin = () =>
  localStorage.getItem("adminInfo") ? JSON.parse(localStorage.getItem("adminInfo")) : null;
const isOnline = () => sessionStorage.getItem("isOnline") === "true";

const ChatPresence = () => {
  const history = useHistory();
  const { pathname } = useLocation();

  // Connect once and (re)register on every (re)connect with the Online/Offline
  // choice. The Dashboard also registers when it mounts — that is harmless.
  useEffect(() => {
    const admin = getAdmin();
    if (!admin?.id) return undefined;
    const handleConnect = () =>
      socket.emit("register", [admin.id, admin.name, "employee", isOnline()]);
    socket.on("connect", handleConnect);
    if (socket.connected) handleConnect();
    else socket.connect();
    return () => socket.off("connect", handleConnect);
  }, []);

  // Offer handler — re-attached after every page change, because the
  // Dashboard's cleanup removes ALL "send_status" listeners when you leave it.
  useEffect(() => {
    const admin = getAdmin();
    if (!admin?.id || pathname.startsWith("/dashboard")) return undefined; // Dashboard handles its own

    const handleOffer = ({ agentId, subuserId, state, website }) => {
      if (!isOnline() || !agentId || window.location.pathname.startsWith("/dashboard")) return;
      const onAccepted = ({ chatId, agentId: acceptedAgentId, state: acceptedState, agentName }) => {
        clearTimeout(fallback);
        socket.off("new_customer", onAccepted);
        // Same keys the Dashboard reads to resume the chat on mount.
        localStorage.setItem("chatId", chatId);
        localStorage.setItem("agentId", acceptedAgentId);
        localStorage.setItem("stateId", acceptedState);
        localStorage.setItem("receiverName", agentName);
        notifySuccess(`New chat from ${agentName || "an agent"} — opening Dashboard`);
        history.push("/dashboard");
      };
      const fallback = setTimeout(() => {
        socket.off("new_customer", onAccepted);
        history.push("/dashboard");
      }, 5000);
      socket.on("new_customer", onAccepted);
      socket.emit("get_status", {
        agentId: Number(agentId),
        employeeId: admin.id,
        employeeName: admin.name,
        type: "accept",
        reason: "",
        website,
        state,
        subuserId,
      });
    };

    socket.on("send_status", handleOffer);
    return () => socket.off("send_status", handleOffer);
  }, [pathname, history]);

  return null;
};

export default ChatPresence;
